// PlansTab.tsx
import React from 'react';
import { Plus } from 'lucide-react';
import { CurrentPlanForm } from './CurrentPlanForm';
import { CurrentPlanList } from './CurrentPlanList';
import { ActionBar } from '../ActionBar';
import { CurrentPlan } from '../../types/data';

interface PlansTabProps {
  currentPlans: CurrentPlan[];
  showForm: boolean;
  editingCurrentPlan?: CurrentPlan;
  onSubmit: (currentPlan: CurrentPlan) => void;
  onEdit: (currentPlan: CurrentPlan) => void;
  onDelete: (id: string) => void;
  onCancel: () => void;
  onClear: () => void;
  onExport: () => void;
  onImport: (csvData: string) => void;
  onGenerateCalendar: () => void;
  setShowForm: (show: boolean) => void;
}

export const PlansTab: React.FC<PlansTabProps> = ({
  currentPlans,
  showForm,
  editingCurrentPlan,
  onSubmit,
  onEdit,
  onDelete,
  onCancel,
  onClear,
  onExport,
  onImport,
  onGenerateCalendar,
  setShowForm,
}) => {
  return (
    <>
      {/* Description Section */}
      <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-md shadow-sm">
        <p className="text-gray-800">
          Add your <strong>Current Plans</strong> from the <em>Regimens</em> tab by clicking the{' '}
          <button className="inline-flex items-center text-green-600 font-medium hover:underline">
            <Plus className="w-4 h-4 mr-1" /> green plus button
          </button>{' '}
          or customize them manually.
        </p>
        <p className="mt-2 text-gray-800">
          Once added, enable the plan, set a start date, and generate calendars to organize dosing and frequency for
          your injections, peptides, and supplements effortlessly.
        </p>
      </div>

      {/* Main Header and Actions */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Current Plans</h1>
        <div className="flex gap-4">
          <button
            onClick={onGenerateCalendar}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors"
          >
            Generate Calendar
          </button>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add Current Plan
          </button>
        </div>
      </div>

      {/* Action Bar */}
      <ActionBar
        onClear={onClear}
        onExport={onExport}
        onImport={onImport}
        onSample={onImport}
        sampleUrl="https://raw.githubusercontent.com/peptidenow/sample-data/refs/heads/main/regimens.csv"
      />

      {/* Form or List */}
      {showForm ? (
        <div className="mb-8">
          <CurrentPlanForm onSubmit={onSubmit} initialData={editingCurrentPlan} onCancel={onCancel} />
        </div>
      ) : (
        <CurrentPlanList savePlan={onSubmit} currentPlans={currentPlans} onEdit={onEdit} onDelete={onDelete} />
      )}
    </>
  );
};
